import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MapPin, ArrowRight, Loader2, Crosshair } from "lucide-react";
import { PRESET_CITIES } from "@/lib/api";
import { useLocation } from "@/context/LocationContext";

/**
 * CityChanger — typeahead city selector + Go button.
 *
 * Props:
 *  - variant: "inline" | "header"
 *  - onGo?: (cityName) => void
 *      Optional override. If provided, called instead of default search navigation.
 *      Default: navigates to /search?city=<city> and updates LocationContext.
 *  - initialCity?: string
 *  - autoDetectFallback?: boolean (show crosshair to use GPS)
 */
export default function CityChanger({
  variant = "inline",
  onGo,
  initialCity,
  autoDetectFallback = true,
}) {
  const { loc, setCity, detect, loading: locLoading } = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // The context's placeholder city (source "default") is not the user's city — leave the box empty.
  const knownCity = loc.source !== "default" ? loc.city : "";
  const initial = initialCity ?? params.get("city") ?? knownCity ?? "";
  const [value, setValue] = useState(initial);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef(null);

  useEffect(() => {
    setValue(initial);
  }, [initial]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const suggestions = useMemo(() => {
    const q = (value || "").trim().toLowerCase();
    if (!q) return PRESET_CITIES.slice(0, 8);
    return PRESET_CITIES.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 8);
  }, [value]);

  const commit = async (cityName) => {
    const raw = (cityName ?? value).trim();
    if (!raw) return;
    setBusy(true);
    try {
      const preset = PRESET_CITIES.find((c) => c.name.toLowerCase() === raw.toLowerCase());
      const updated = await setCity(raw, preset?.lat, preset?.lng);
      setOpen(false);
      const finalCity = updated?.city || raw;
      if (onGo) {
        onGo(finalCity);
      } else {
        const sp = new URLSearchParams(window.location.search);
        sp.set("city", finalCity);
        navigate(`/search?${sp.toString()}`);
      }
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const pick = activeIdx >= 0 ? suggestions[activeIdx]?.name : value;
      commit(pick);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const useGps = async () => {
    const r = await detect();
    if (r?.city) commit(r.city);
  };

  const inputBase =
    variant === "header"
      ? "bg-stone-50 rounded-l-full px-3 py-2 text-sm outline-none w-36 md:w-44"
      : "bg-stone-50 rounded-l-xl px-3 py-2 text-sm outline-none w-full";

  const btnBase =
    variant === "header"
      ? "rounded-r-full bg-[#2D5A27] hover:bg-[#24491f] text-white px-3 py-2 text-xs font-bold flex items-center gap-1"
      : "rounded-r-xl bg-[#2D5A27] hover:bg-[#24491f] text-white px-3 py-2 text-xs font-bold flex items-center gap-1";

  return (
    <div ref={wrapRef} className="relative" data-testid={`city-changer-${variant}`}>
      <div className="flex items-stretch">
        <div className={`flex items-center gap-1.5 ${variant === "header" ? "border border-stone-200 rounded-l-full pl-2 bg-stone-50" : "border border-stone-200 rounded-l-xl pl-2 bg-stone-50"}`}>
          <MapPin size={14} className="text-[#E05E36]" />
          <input
            value={value}
            onChange={(e) => { setValue(e.target.value); setOpen(true); setActiveIdx(-1); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKey}
            placeholder="Change city…"
            className={inputBase}
            data-testid={`city-input-${variant}`}
            aria-label="City"
            autoComplete="off"
          />
        </div>
        <button
          onClick={() => commit()}
          disabled={busy || !value.trim()}
          className={`${btnBase} disabled:opacity-50 disabled:cursor-not-allowed`}
          data-testid={`city-go-${variant}`}
          aria-label="Go to this city"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
          <span className="hidden sm:inline">Go</span>
        </button>
      </div>

      {open && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 mt-1 bg-white border border-stone-200 rounded-xl shadow-lg z-[60] max-h-72 overflow-auto" data-testid={`city-suggestions-${variant}`}>
          {autoDetectFallback && (
            <button
              onClick={useGps}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-stone-700 hover:bg-stone-50 border-b border-stone-100"
              data-testid={`city-usegps-${variant}`}
            >
              <Crosshair size={13} className={`text-[#2D5A27] ${locLoading ? "animate-spin" : ""}`} />
              Use my GPS location
            </button>
          )}
          {suggestions.map((c, i) => (
            <button
              key={c.name}
              onMouseDown={(e) => { e.preventDefault(); commit(c.name); }}
              onMouseEnter={() => setActiveIdx(i)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-stone-50 flex items-center justify-between ${activeIdx === i ? "bg-stone-50" : ""}`}
              data-testid={`city-option-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <span className="text-stone-800">{c.name}</span>
              <MapPin size={12} className="text-stone-400" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
